import { useCallback } from "react";
import type { ActiveRunSummary } from "../../api/types.js";
import { stopRun as stopRunRequest } from "./runApi.js";

type ChatStreamInput = {
  sessionId?: string;
  projectId?: string | null;
  prompt: string;
  selectedSkill?: string | null;
  thinkingMode?: "thinking" | "fast";
  askResponse?: {
    assistantMessageId: string;
    answers: any[];
  };
};

type ChatStreamCallbacks = {
  onOpened?: () => void;
  onRunStarted?: (data: any) => void;
  onSessionTitleUpdated?: (data: any) => void;
  onAssistantSegmentStarted?: (data: any) => void;
  onAssistantSegmentClassified?: (data: any) => void;
  onContentReset?: (data: any) => void;
  onAssistantSegmentFinished?: (data: any) => void;
  onProcessItemStarted?: (data: any) => void;
  onProcessItemUpdated?: (data: any) => void;
  onProcessItemCompleted?: (data: any) => void;
  onContentPart?: (data: any) => void;
  onDelta?: (data: any) => void;
  onReasoningDelta?: (data: any) => void;
  onActivity?: (data: any) => void;
  onCitation?: (data: any) => void;
  onAskUserQuestion?: (data: any) => void;
  onCompleted?: (data: any) => void;
  onFailed?: (data: any) => void;
  onCancelled?: (data: any) => void;
};

export type ChatStreamTerminal = "completed" | "failed" | "cancelled" | null;

export class ActiveRunConflictError extends Error {
  readonly activeRun: ActiveRunSummary;

  constructor(activeRun: ActiveRunSummary, message?: string) {
    super(message || "当前已有一个对话正在处理。");
    this.name = "ActiveRunConflictError";
    this.activeRun = activeRun;
  }
}

export function useChatStream() {
  const startChatStream = useCallback(async (input: ChatStreamInput, callbacks: ChatStreamCallbacks, signal?: AbortSignal) => {
    const response = await fetch("/api/chat/stream", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...input, clientCapabilities: { assistantSegments: true, processItems: true, chatStreamVersion: 2 } }),
      signal
    });
    if (!response.ok || !response.body) {
      const data: any = await response.json().catch(() => ({}));
      if (response.status === 409 && data?.error === "active_run_conflict" && data.activeRun) {
        throw new ActiveRunConflictError(data.activeRun as ActiveRunSummary, typeof data.message === "string" ? data.message : undefined);
      }
      throw new Error(typeof data?.error === "string" && data.error ? data.error : "stream failed");
    }
    callbacks.onOpened?.();
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let streamTerminal: ChatStreamTerminal = null;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() || "";
      for (const frame of frames) {
        const eventName = frame.match(/^event:\s*(.+)$/m)?.[1];
        const dataText = frame.match(/^data:\s*([\s\S]*)$/m)?.[1];
        if (!eventName || !dataText) continue;
        const data = JSON.parse(dataText);
        if (eventName === "run_started") callbacks.onRunStarted?.(data);
        if (eventName === "session_title_updated") callbacks.onSessionTitleUpdated?.(data);
        if (eventName === "assistant_segment_started") callbacks.onAssistantSegmentStarted?.(data);
        if (eventName === "assistant_segment_classified") callbacks.onAssistantSegmentClassified?.(data);
        if (eventName === "content_reset") callbacks.onContentReset?.(data);
        if (eventName === "assistant_segment_finished") callbacks.onAssistantSegmentFinished?.(data);
        if (eventName === "process_item_started") callbacks.onProcessItemStarted?.(data);
        if (eventName === "process_item_updated") callbacks.onProcessItemUpdated?.(data);
        if (eventName === "process_item_completed") callbacks.onProcessItemCompleted?.(data);
        if (eventName === "content_part") callbacks.onContentPart?.(data);
        if (eventName === "delta") callbacks.onDelta?.(data);
        if (eventName === "reasoning_delta") callbacks.onReasoningDelta?.(data);
        if (eventName === "activity") callbacks.onActivity?.(data);
        if (eventName === "citation") callbacks.onCitation?.(data);
        if (eventName === "ask_user_question") callbacks.onAskUserQuestion?.(data);
        if (eventName === "completed") {
          streamTerminal = "completed";
          callbacks.onCompleted?.(data);
        }
        if (eventName === "failed") {
          streamTerminal = "failed";
          callbacks.onFailed?.(data);
        }
        if (eventName === "cancelled") {
          streamTerminal = "cancelled";
          callbacks.onCancelled?.(data);
        }
      }
    }
    return streamTerminal;
  }, []);

  const stopChatRun = useCallback(async (input: { runId: string; sessionId: string }) => {
    return stopRunRequest(input);
  }, []);

  return { startChatStream, stopChatRun };
}
